import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, Download, FileText, ShoppingCart } from "lucide-react";
import { DocumentRow } from "../components/ui/DocumentRow";
import { EmptyState } from "../components/ui/EmptyState";
import { ListCapNotice } from "../components/ui/ListCapNotice";
import { PageHeader } from "../components/ui/PageHeader";
import { PortalState } from "../components/ui/PortalState";
import { apiGet, downloadBlob, PortalApiError } from "../api/portal-client";
import { usePortalResource } from "../hooks/usePortalResource";
import { DASH, formatDay, formatEuro, formatQuantity } from "../lib/format";

interface Payment {
  isPaid: boolean;
  label: string;
  montantPaye: number;
  soldeRestant: number;
  paidAt: string | null;
  overdue: boolean;
}

interface LinkedDeliveryNote {
  id: string;
  blNumber: string | null;
  signedAt: string | null;
}

interface LinkedOrder {
  id: string;
  orderNumber: string | null;
  orderDate: string;
}

interface Facture {
  id: string;
  numero: string | null;
  dateEmission: string;
  totalTTC: string | number;
  statut: string;
  downloaded: boolean;
  payment: Payment;
  signedDeliveryNotes: LinkedDeliveryNote[];
  orders: LinkedOrder[];
}

interface FactureLigne {
  id: string;
  designation: string;
  gencode: string | null;
  quantite: string | number;
  uniteMesure: string | null;
  prixUnitaireHT: string | number;
  montantHT: string | number;
}

interface FactureDetail {
  id: string;
  lignes: FactureLigne[];
}

/** Plafond serveur de `/me/factures` (portal-auth.routes.ts). */
const FACTURES_CAP = 100;

/**
 * Mes factures.
 *
 * W7-B — « Aucune facture pour l'instant » s'affichait dès que le chargement
 * retombait, y compris après une coupure réseau ou un 403 : sur des documents
 * COMPTABLES, laisser croire à un client qu'il n'a rien à payer est le pire des
 * messages. L'état est désormais nommé et le vide n'est atteignable qu'après une
 * lecture réussie. Les montants absents s'écrivent « — » : `Number(null)` vaut
 * 0 et affichait « 0,00 € » — une facture soldée par accident.
 */
export function InvoicesPage() {
  const { data, status, error, reload } = usePortalResource<Facture[]>(() =>
    apiGet<Facture[]>("/me/factures"),
  );
  // Copie locale : elle porte la coche « déjà téléchargée », posée après coup.
  const [list, setList] = useState<Facture[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, FactureLigne[]>>({});
  const [detailError, setDetailError] = useState<string | null>(null);

  useEffect(() => {
    if (data) setList(data);
  }, [data]);

  async function handleDownload(f: Facture) {
    await downloadBlob(`/me/factures/${f.id}/pdf`, `facture-${f.numero ?? f.id.slice(-8)}.pdf`);
    setList((prev) => prev.map((x) => (x.id === f.id ? { ...x, downloaded: true } : x)));
  }

  async function toggleDetail(f: Facture) {
    if (expanded === f.id) {
      setExpanded(null);
      return;
    }
    setExpanded(f.id);
    setDetailError(null);
    if (details[f.id]) return;
    try {
      const detail = await apiGet<FactureDetail>(`/me/factures/${f.id}`);
      setDetails((prev) => ({ ...prev, [f.id]: detail.lignes }));
    } catch (e: unknown) {
      setDetailError(e instanceof PortalApiError ? e.message : "Détail indisponible.");
    }
  }

  return (
    <main className="max-w-3xl mx-auto px-4 py-6">
      <PageHeader title="Mes factures" subtitle="Consultez et téléchargez vos factures." />

      <PortalState status={status} label="vos factures" error={error} onRetry={reload} />

      {status === "ready" && list.length === 0 && (
        <EmptyState
          icon={FileText}
          title="Aucune facture pour l'instant"
          description="Vos factures apparaîtront ici dès que votre fournisseur en émettra une."
          action={{ to: "/messages", label: "Écrire à mon fournisseur" }}
        />
      )}

      {status === "ready" && list.length > 0 && (
        <>
          <div className="bg-card border border-border rounded-xl2 overflow-hidden">
            {list.map((f) => (
              <div key={f.id} className="border-b border-border last:border-0">
                <DocumentRow
                  title={f.numero ?? f.id.slice(-8)}
                  subtitle={formatDay(f.dateEmission)}
                  amount={formatEuro(f.totalTTC)}
                  downloaded={f.downloaded}
                  onDownload={() => handleDownload(f)}
                  rightExtra={
                    <span
                      className={`shrink-0 text-xs rounded-full px-2 py-0.5 ${
                        f.payment.isPaid
                          ? "bg-success/10 text-success"
                          : f.payment.overdue
                            ? "bg-danger/10 text-danger"
                            : "bg-terracotta/10 text-terracotta"
                      }`}
                    >
                      {f.payment.label}
                    </span>
                  }
                />

                <div className="px-4 pb-3 -mt-1 flex flex-col gap-1.5">
                  {!f.payment.isPaid && f.payment.soldeRestant > 0 && (
                    <p className="text-xs text-muted">
                      Reste à régler :{" "}
                      <span className="font-mono text-ink">{formatEuro(f.payment.soldeRestant)}</span>
                    </p>
                  )}

                  {f.signedDeliveryNotes.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2">
                      {f.signedDeliveryNotes.map((bl) => (
                        <button
                          key={bl.id}
                          type="button"
                          onClick={() =>
                            downloadBlob(`/me/bl/${bl.id}/pdf`, `BL-${bl.blNumber ?? bl.id.slice(-8)}.pdf`)
                          }
                          className="inline-flex items-center gap-1 text-xs text-terracotta hover:underline"
                        >
                          <Download size={13} /> BL signé n°{bl.blNumber ?? bl.id.slice(-8)} (PDF)
                        </button>
                      ))}
                    </div>
                  )}

                  {f.orders.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2">
                      {f.orders.map((o) => (
                        <Link
                          key={o.id}
                          to="/orders"
                          className="inline-flex items-center gap-1 text-xs text-muted hover:text-terracotta"
                        >
                          <ShoppingCart size={13} /> Commande n°{o.orderNumber ?? o.id.slice(-8)}
                        </Link>
                      ))}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => toggleDetail(f)}
                    aria-expanded={expanded === f.id}
                    className="inline-flex items-center gap-1 self-start text-xs text-muted hover:text-terracotta"
                  >
                    {expanded === f.id ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                    Détail des lignes
                  </button>

                  {expanded === f.id && (
                    <div className="mt-1">
                      {detailError && (
                        <p role="alert" className="text-xs text-danger">
                          {detailError}
                        </p>
                      )}
                      {!detailError && !details[f.id] && (
                        <p role="status" className="text-xs text-muted">
                          Chargement du détail…
                        </p>
                      )}
                      {details[f.id]?.length === 0 && (
                        <p className="text-xs text-muted">Aucune ligne sur cette facture.</p>
                      )}
                      {details[f.id] && details[f.id].length > 0 && (
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-xs text-muted">
                              <th className="text-left font-medium py-1">Désignation</th>
                              <th className="text-left font-medium py-1">Gencode</th>
                              <th className="text-right font-medium py-1">Qté</th>
                              <th className="text-right font-medium py-1">Montant HT</th>
                            </tr>
                          </thead>
                          <tbody>
                            {details[f.id].map((l) => (
                              <tr key={l.id} className="border-t border-border">
                                <td className="py-1.5 text-ink">{l.designation}</td>
                                <td className="py-1.5 text-xs font-mono text-muted">
                                  {l.gencode ?? DASH}
                                </td>
                                <td className="py-1.5 text-right text-muted">
                                  {formatQuantity(l.quantite, l.uniteMesure)}
                                </td>
                                <td className="py-1.5 text-right font-mono text-ink">
                                  {formatEuro(l.montantHT)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <ListCapNotice shown={list.length} cap={FACTURES_CAP} label="factures" genre="f" />
        </>
      )}
    </main>
  );
}
